<template>
  <b-card
    aria-label="Formulaire de génération de facture fournisseur"
    role="form"
  >
    <div
      v-if="isLoading === true"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Text Centered"
      />
    </div>

    <!-- Day Filter Section -->
    <b-form-group
      v-if="!isLoading"
      class="mb-3"
    >
      <div class="day-selector-wrapper">
        <div class="day-selector">
          <!-- All Days Button -->
          <div
            :class="['day-card', { 'selected': selectedDay === null }]"
            @click="selectDay(null)"
          >
            <feather-icon
              icon="LayersIcon"
              size="18"
              class="mb-1"
            />
            <div class="day-label">
              Tous
            </div>
          </div>

          <!-- Individual Day Cards -->
          <div
            v-for="day in weekdays"
            :key="day.value"
            :class="['day-card', { 'selected': selectedDay === day.value }]"
            @click="selectDay(day.value)"
          >
            <feather-icon
              icon="CalendarIcon"
              size="18"
              class="mb-1"
            />
            <div class="day-label">
              {{ day.label }}
            </div>
          </div>
        </div>
      </div>

      <!-- Supplier Count Feedback -->
      <small
        v-if="!isLoading"
        class="text-muted mt-2 d-block"
      >
        <feather-icon
          icon="InfoIcon"
          size="14"
          class="mr-1"
        />
        {{ suppliers.length }} fournisseur{{ suppliers.length > 1 ? 's' : '' }} disponible{{ suppliers.length > 1 ? 's' : '' }}
        <span v-if="selectedDay"> pour {{ getSelectedDayLabel() }}</span>
      </small>
    </b-form-group>

    <!-- End Date Filter -->
    <b-form-group
      v-if="!isLoading"
      label="Date de fin (les commandes livrées jusqu'à cette date)"
      label-for="end-date"
      class="mb-3"
    >
      <b-form-datepicker
        id="end-date"
        v-model="endDate"
        placeholder="Toutes les dates"
        today-button
        reset-button
        locale="fr"
        :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
        @input="onEndDateChange"
      />
      <small class="text-muted mt-1 d-block">
        <feather-icon
          icon="CalendarIcon"
          size="14"
          class="mr-1"
        />
        Filtrer les commandes livrées par date. Les retours fournisseur restent inclus sans filtre de date.
      </small>
    </b-form-group>

    <!-- Blocked Suppliers Notice -->
    <div
      v-if="!isLoading && blockInvoicesWithoutDeliveredOrders && disabledSupplierCount > 0"
      class="mb-3"
    >
      <div class="d-flex align-items-center text-warning small">
        <feather-icon
          icon="AlertCircleIcon"
          size="14"
          class="mr-1"
        />
        <span>
          <strong>{{ disabledSupplierCount }}</strong>
          fournisseur{{ disabledSupplierCount > 1 ? 's' : '' }} ne {{ disabledSupplierCount > 1 ? 'peuvent' : 'peut' }} pas être sélectionné{{ disabledSupplierCount > 1 ? 's' : '' }} aujourd'hui.
        </span>
        <b-button
          v-b-toggle.blocked-suppliers-detail
          variant="link"
          size="sm"
          class="p-0 ml-1 text-decoration-none"
        >
          Voir le détail
        </b-button>
      </div>
      <b-collapse id="blocked-suppliers-detail">
        <b-list-group class="mt-1">
          <b-list-group-item
            v-for="supplier in disabledSuppliers"
            :key="supplier.id"
            class="py-1 px-2 d-flex justify-content-between align-items-center small"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                icon="UserIcon"
                size="14"
                class="text-muted mr-1"
              />
              <span>{{ supplier.username }}</span>
            </div>
            <small class="text-muted">
              {{ supplier.return_count || 0 }} retour{{ (supplier.return_count || 0) > 1 ? 's' : '' }} / 0 livré
            </small>
          </b-list-group-item>
        </b-list-group>
      </b-collapse>
    </div>

    <b-form-group
      v-if="!isLoading"
      label="Fournisseur"
      label-for="fournisseur"
      aria-label="Sélection du fournisseur"
      description="Sélectionnez un ou plusieurs fournisseurs pour générer des factures."
    >
      <div class="supplier-select-toolbar mb-1">
        <b-form-checkbox
          :checked="allSelectableSuppliersSelected"
          :indeterminate="someSelectableSuppliersSelected && !allSelectableSuppliersSelected"
          :disabled="selectableSuppliers.length === 0"
          switch
          @change="toggleAllSuppliers"
        >
          Sélectionner tous les fournisseurs générables
        </b-form-checkbox>
        <b-badge
          variant="light-primary"
          class="supplier-count-badge"
        >
          {{ selectedSuppliers.length }} / {{ selectableSuppliers.length }} sélectionné{{ selectedSuppliers.length > 1 ? 's' : '' }}
        </b-badge>
      </div>

      <v-select
        v-model="selectedSuppliers"
        :multiple="true"
        placeholder="Sélectionner un ou plusieurs fournisseurs"
        label="username"
        :options="suppliers"
        :selectable="isSupplierSelectable"
        input-id="fournisseur"
        aria-label="Liste des fournisseurs disponibles"
      >
        <template #option="option">
          <div
            :class="[
              'd-flex',
              'align-items-center',
              'justify-content-between',
              'w-100',
              'supplier-option',
              { 'supplier-option-blocked': isSupplierBlocked(option) }
            ]"
            :title="getSupplierBlockedTitle(option)"
          >
            <div class="d-flex align-items-center supplier-option-name">
              <b-form-checkbox
                :checked="isSupplierSelected(option)"
                :disabled="!isSupplierSelectable(option)"
                class="supplier-option-checkbox mr-1"
                @change="toggleSupplierSelection(option)"
                @click.native.stop
              />
              <feather-icon
                :icon="getSupplierOptionIcon(option)"
                size="18"
                :class="isSupplierBlocked(option) ? 'text-danger mr-1' : 'text-primary mr-1'"
              />
              <div class="supplier-option-copy">
                <span
                  :class="{ 'supplier-blocked-text': isSupplierBlocked(option) }"
                >
                  {{ option.username }}
                </span>
                <small
                  v-if="option.can_generate_invoice === false && option.blocking_reason === 'pending_supplier_validation'"
                  class="d-block text-warning font-weight-bold"
                >
                  Prochaine facture générable après validation fournisseur
                </small>
                <small
                  v-else-if="option.can_generate_invoice === false && option.blocking_reason === 'draft_invoice'"
                  class="d-block text-warning font-weight-bold"
                >
                  Brouillon de facture en attente
                </small>
              </div>
            </div>
            <div class="d-flex align-items-center supplier-option-meta">
              <div class="d-flex flex-column align-items-end">
                <div
                  v-if="option.can_generate_invoice === false"
                  class="small text-primary font-weight-bold"
                >
                  Prochain net: {{ formatMoney(option.next_payment_net_a_payer) }} TND
                </div>
                <div class="d-flex align-items-center">
                  <feather-icon
                    icon="PackageIcon"
                    size="14"
                    :class="option.delivered_count > 0 ? 'text-success' : 'text-muted'"
                    class="mr-50"
                  />
                  <span
                    :class="[
                      'small',
                      'font-weight-bold',
                      option.delivered_count > 0 ? 'text-success' : 'text-muted'
                    ]"
                  >
                    {{ option.delivered_count || 0 }} livré{{ (option.delivered_count || 0) > 1 ? 's' : '' }}
                  </span>
                </div>
                <div
                  v-if="option.return_count > 0"
                  class="d-flex align-items-center"
                >
                  <feather-icon
                    icon="CornerUpLeftIcon"
                    size="14"
                    class="text-warning mr-50"
                  />
                  <span class="small text-muted">
                    {{ option.return_count }} retour{{ option.return_count > 1 ? 's' : '' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </v-select>
    </b-form-group>
    <b-form-group>
      <span id="save-invoice-btn-wrapper">
        <b-button
          ref="saveButton"
          variant="primary"
          class="save-btn"
          :class="{ 'not-allowed': isLoading || !selectedSuppliers || selectedSuppliers.length === 0 }"
          aria-label="Générer la facture pour le fournisseur sélectionné"
          :disabled="isLoading || !selectedSuppliers || selectedSuppliers.length === 0"
          @click="generateInvoices"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-1"
            aria-hidden="true"
          />
          {{ selectedSuppliers && selectedSuppliers.length > 1 ? `Générer ${selectedSuppliers.length} factures` : 'Générer la facture' }}
        </b-button>
        <b-tooltip
          v-if="isLoading || !selectedSuppliers || selectedSuppliers.length === 0"
          target="save-invoice-btn-wrapper"
          triggers="hover focus"
          placement="top"
        >
          <template v-if="isLoading">
            Veuillez patienter, chargement des fournisseurs…
          </template>
          <template v-else-if="!selectedSuppliers || selectedSuppliers.length === 0">
            Veuillez sélectionner au moins un fournisseur pour générer une facture.
          </template>
        </b-tooltip>
      </span>
      <div
        id="create-invoice-help"
        class="sr-only"
      >
        Utilisez ce formulaire pour générer une facture fournisseur. Sélectionnez le fournisseur concerné puis cliquez sur "Générer la facture".
      </div>
      <b-alert
        v-model="alertVisible"
        :show="alertVisible"
        :variant="alertVariant"
        dismissible
        class="mt-3"
      >
        {{ alertMessage }}
      </b-alert>
    </b-form-group>
  </b-card>
</template>

<script>
import vSelect from 'vue-select'
import {
  BCard, BFormGroup, BButton, BFormDatepicker, BTooltip, BAlert, BCollapse, BListGroup, BListGroupItem, VBToggle, BFormCheckbox, BBadge,
} from 'bootstrap-vue'
import axios from 'axios'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'CreateInvoice',
  directives: {
    'b-toggle': VBToggle,
  },
  components: {
    vSelect,
    BCard,
    BFormGroup,
    BButton,
    BFormDatepicker,
    BTooltip,
    BAlert,
    BCollapse,
    BListGroup,
    BListGroupItem,
    BBadge,
    BFormCheckbox,
  },
  data() {
    return {
      selectedSuppliers: [],
      suppliers: [],
      selectedDay: null,
      weekdays: [
        { value: 'monday', short: 'Lun', label: 'Lundi' },
        { value: 'tuesday', short: 'Mar', label: 'Mardi' },
        { value: 'wednesday', short: 'Mer', label: 'Mercredi' },
        { value: 'thursday', short: 'Jeu', label: 'Jeudi' },
        { value: 'friday', short: 'Ven', label: 'Vendredi' },
        { value: 'saturday', short: 'Sam', label: 'Samedi' },
        { value: 'sunday', short: 'Dim', label: 'Dimanche' },
      ],
      orders: [],
      user: {},
      isLoading: false,
      alertVisible: false,
      alertVariant: '',
      alertMessage: '',
      endDate: null,
      blockInvoicesWithoutDeliveredOrders: false,
    }
  },
  computed: {
    selectableSuppliers() {
      return this.suppliers.filter(supplier => this.isSupplierSelectable(supplier))
    },
    selectedSupplierIds() {
      return new Set((this.selectedSuppliers || []).map(supplier => supplier.id))
    },
    allSelectableSuppliersSelected() {
      return this.selectableSuppliers.length > 0
        && this.selectableSuppliers.every(supplier => this.selectedSupplierIds.has(supplier.id))
    },
    someSelectableSuppliersSelected() {
      return this.selectableSuppliers.some(supplier => this.selectedSupplierIds.has(supplier.id))
    },
    disabledSuppliers() {
      if (!this.blockInvoicesWithoutDeliveredOrders) {
        return []
      }
      return this.suppliers.filter(s => !s.delivered_count || s.delivered_count === 0)
    },
    disabledSupplierCount() {
      return this.disabledSuppliers.length
    },
  },
  async created() {
    this.user = storeAuth.state.user
    await storeAuth.dispatch('fetchCompanyInfo')
    this.updateBlockConfig()
    this.getSuppliers()
  },
  methods: {
    selectDay(day) {
      this.selectedDay = day
      this.selectedSuppliers = [] // Reset supplier selection when day changes
      this.getSuppliers()
    },

    onEndDateChange() {
      this.selectedSuppliers = [] // Reset supplier selection when date changes
      this.getSuppliers()
    },

    updateBlockConfig() {
      const { company } = storeAuth.state
      this.blockInvoicesWithoutDeliveredOrders = company && company.allow_invoices_without_delivered_orders === false
    },

    isSupplierSelectable(option) {
      return option && !this.isSupplierBlocked(option)
    },
    isSupplierSelected(option) {
      return Boolean(option && this.selectedSupplierIds.has(option.id))
    },
    toggleSupplierSelection(option) {
      if (!this.isSupplierSelectable(option)) return

      if (this.isSupplierSelected(option)) {
        this.selectedSuppliers = this.selectedSuppliers.filter(supplier => supplier.id !== option.id)
      } else {
        this.selectedSuppliers = [...this.selectedSuppliers, option]
      }
    },
    toggleAllSuppliers(checked) {
      this.selectedSuppliers = checked ? [...this.selectableSuppliers] : []
    },

    isSupplierBlocked(option) {
      if (!option) {
        return true
      }
      if (option.can_generate_invoice === false) {
        return true
      }
      return this.blockInvoicesWithoutDeliveredOrders && (!option.delivered_count || option.delivered_count === 0)
    },

    getSupplierOptionIcon(option) {
      if (this.isSupplierBlocked(option)) {
        return option && option.blocking_reason === 'pending_supplier_validation' ? 'ClockIcon' : 'XCircleIcon'
      }
      return 'UserIcon'
    },

    getSupplierBlockedTitle(option) {
      if (!option) {
        return ''
      }
      if (option.blocking_reason === 'pending_supplier_validation') {
        return 'Cette prochaine facture pourra être générée après validation fournisseur.'
      }
      if (option.blocking_reason === 'draft_invoice') {
        return 'Impossible de générer une nouvelle facture : un brouillon de facture existe déjà pour ce fournisseur.'
      }
      if (this.blockInvoicesWithoutDeliveredOrders && (!option.delivered_count || option.delivered_count === 0)) {
        return 'Impossible de générer une facture : ce fournisseur n\'a aucun colis livré (uniquement des retours).'
      }
      return ''
    },

    formatMoney(value) {
      const amount = Number(value || 0)
      return amount.toFixed(3)
    },

    getSelectedDayLabel() {
      if (!this.selectedDay) return ''
      const day = this.weekdays.find(d => d.value === this.selectedDay)
      return day ? day.label.toLowerCase() : ''
    },

    async generateInvoices() {
      if (!this.selectedSuppliers || this.selectedSuppliers.length === 0) {
        this.alertVariant = 'danger'
        this.alertMessage = 'Veuillez sélectionner au moins un fournisseur.'
        this.alertVisible = true
        return
      }

      // Validation guard: check if any selected supplier has 0 delivered orders when blocking is enabled
      if (this.blockInvoicesWithoutDeliveredOrders) {
        const invalidSuppliers = this.selectedSuppliers.filter(s => !s.delivered_count || s.delivered_count === 0)
        if (invalidSuppliers.length > 0) {
          const supplierNames = invalidSuppliers.map(s => s.username).join(', ')
          this.alertVariant = 'danger'
          this.alertMessage = `Impossible de générer une facture pour ${invalidSuppliers.length > 1 ? 'les fournisseurs' : 'le fournisseur'} suivant${invalidSuppliers.length > 1 ? 's' : ''} : ${supplierNames}. ${invalidSuppliers.length > 1 ? 'Ces fournisseurs n\'ont' : 'Ce fournisseur n\'a'} aucun colis livré. La configuration actuelle exige au moins un colis livré pour créer une facture.`
          this.alertVisible = true
          this.showToast('error', 'top-center', 'Facturation bloquée : au moins un colis livré requis')
          return
        }
      }

      const blockedSuppliers = this.selectedSuppliers.filter(s => s.can_generate_invoice === false)
      if (blockedSuppliers.length > 0) {
        const supplierNames = blockedSuppliers.map(s => s.username).join(', ')
        this.alertVariant = 'danger'
        this.alertMessage = `Impossible de générer la prochaine facture pour: ${supplierNames}. Elle pourra être générée après validation fournisseur.`
        this.alertVisible = true
        this.showToast('error', 'top-center', 'Facturation bloquée : validation fournisseur requise')
        return
      }

      // Persist end date for preview pages
      if (this.endDate) {
        sessionStorage.setItem('invoiceEndDate', this.endDate)
      } else {
        sessionStorage.removeItem('invoiceEndDate')
      }

      // Dasco invoices always use the manual preview so the accountant can
      // choose the exact delivered and returned orders before confirmation.
      const supplierIds = this.selectedSuppliers.map(supplier => supplier.id)
      sessionStorage.setItem('batchSupplierIds', JSON.stringify(supplierIds))
      sessionStorage.setItem('invoiceGenerationMode', 'manual')
      this.$router.push({ name: 'batch-invoice-preview' })
    },

    // No longer needed
    // withTitle() {
    //   this.$router.push(`/invoices/preview/${this.supplier.id}`)
    // },
    async getSuppliers() {
      this.isLoading = true
      try {
        const params = {}
        if (this.selectedDay) {
          params.day = this.selectedDay
        }
        if (this.endDate) {
          params.delivered_at__lte = this.endDate
        }
        params.include_blocked = true
        const { data } = await axios.get('/api/auth/supplier-allowed-for-invoicing/', { params })
        this.suppliers = data
        this.selectedSuppliers = this.selectedSuppliers.filter(selected => (
          this.suppliers.some(supplier => supplier.id === selected.id && this.isSupplierSelectable(supplier))
        ))
        this.autoSelectSupplierFromQuery()
      } catch (error) {
        console.error('Error fetching suppliers:', error)
        this.alertVariant = 'danger'
        this.alertMessage = error.response?.data?.day?.[0] || 'Erreur lors du chargement des fournisseurs.'
        this.alertVisible = true
        this.suppliers = []
      } finally {
        this.isLoading = false
      }
      // No need to auto-select or fetch orders here
    },

    // Pre-select a supplier from the ?supplier=<id> query param when navigating
    // from the supplier detail "Factures" tab CTA. Uses the full object
    // (selectedSuppliers holds objects because the v-select has no `reduce`).
    autoSelectSupplierFromQuery() {
      const supplierId = this.$route.query.supplier
      if (supplierId == null) return
      const id = parseInt(supplierId, 10)
      if (Number.isNaN(id) || !this.suppliers || this.suppliers.length === 0) return
      if (this.selectedSuppliers && this.selectedSuppliers.length > 0) return
      const match = this.suppliers.find(s => s.id === id)
      if (match) {
        this.selectedSuppliers = [match]
      }
    },

    // No longer needed: fetching orders is handled in Preview.vue
    // async getOrdersForSupplier(supplierId) {
    //   ...
    // },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: variant === 'error' ? 'AlertCircleIcon' : 'InfoIcon',
            text,
            variant: variant === 'error' ? 'danger' : variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.day-selector-wrapper {
  margin-bottom: 1rem;
}

.day-selector {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding: 0.5rem 0 1.5rem 0;

  /* Hide scrollbar for cleaner look */
  scrollbar-width: thin;
  scrollbar-color: rgba(234, 84, 85, 0.28) transparent;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(234, 84, 85, 0.28);
    border-radius: 3px;
  }
}

.day-card {
  flex: 1; /* Equal width for all cards */
  min-width: 100px;
  height: 90px;
  padding: 0.75rem;
  border: 1px solid transparent;
  border-radius: 16px;
  background: white;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  text-align: center;
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(234, 84, 85, 0.22);
  }

  /* Icon styling */
  .feather-icon {
    color: #e0111a;
    background: rgba(234, 84, 85, 0.10);
    padding: 8px;
    border-radius: 12px;
    width: 36px;
    height: 36px;
    transition: all 0.3s ease;
    margin-bottom: 8px;
  }

  &.selected {
    background: linear-gradient(135deg, rgba(234, 84, 85, 0.95) 0%, rgba(224, 17, 26, 0.95) 100%);
    box-shadow: 0 8px 20px rgba(234, 84, 85, 0.35);

    .day-label {
      color: white !important;
      font-weight: 700;
    }

    .feather-icon {
      color: #e0111a;
      background: white;
      transform: scale(1.15);
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    }

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 25px rgba(234, 84, 85, 0.45);
    }
  }
}

.day-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #5e5873;
  white-space: nowrap;
  transition: color 0.3s ease;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .day-selector {
    gap: 0.5rem;
  }

  .day-card {
    min-width: 85px;
    height: 80px;
    padding: 0.5rem;

    .feather-icon {
      width: 30px;
      height: 30px;
      padding: 6px;
      margin-bottom: 6px;
    }
  }

  .day-label {
    font-size: 0.8rem;
  }
}

@media (max-width: 576px) {
  .day-selector {
    gap: 0.4rem;
  }

  .day-card {
    min-width: 70px;
    height: 75px;
    padding: 0.4rem;

    .feather-icon {
      width: 26px;
      height: 26px;
      padding: 5px;
    }
  }

.day-label {
  font-size: 0.7rem;
}
}

/* Supplier option styling */
.supplier-option {
  padding: 0.5rem 0;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(237, 25, 25, 0.05);
  }
}

.supplier-option-blocked {
  opacity: 0.6;
  cursor: not-allowed !important;
  background-color: #f8f8f8 !important;

  &:hover {
    background-color: #f8f8f8 !important;
  }
}

.supplier-blocked-text {
  color: #6c757d !important;
}

.supplier-option-meta {
  white-space: nowrap;
  min-width: 120px;
  text-align: right;
}

.supplier-option-name {
  flex: 1;
  min-width: 0;
}
.supplier-option {
  cursor: pointer;
}

.supplier-select-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.supplier-count-badge {
  white-space: nowrap;
}

.supplier-option-checkbox {
  flex: 0 0 auto;
  margin-bottom: 0;
}

</style>
